import { z } from 'zod';
import { MultiPolygonSchema } from './geojson';

/**
 * Extreme Weather Warning feature (Phase EWW / L.0 Layer #2 blending) — the
 * operator-visible subset of the CAP alert ingest (`cap_alerts`, written by the
 * E.1.6 NAAD CAP worker). GET /api/cap-alerts/extreme returns the LIVE,
 * Actual-status, Severe/Extreme alerts that carry a renderable MultiPolygon, so
 * they can be drawn alongside the AI-fused high-risk hotspots as one "Urgent
 * Anomalies" surface.
 *
 * Layer #2 ("Urgent Anomalies") is MULTI-SOURCE: AI-fused hotspots (points) +
 * these CAP polygons render together. The L.1 Weather-group "Extreme Weather
 * Warnings" toggle mutes ONLY this CAP subset (the hotspots remain).
 *
 * Only the English render-language rows are surfaced (the FR rows are duplicate
 * geometry for the same physical alert, and the event-keyword colour map is
 * English); FR-only alerts (rare, Québec-local) fall through and are a known gap.
 */

/** CAP severity values we surface on the map (Minor/Moderate stay background). */
export const CapSeveritySchema = z.enum(['Severe', 'Extreme']);
export type CapSeverity = z.infer<typeof CapSeveritySchema>;

export const CapAlertPropertiesSchema = z.object({
  id: z.string().uuid(),
  /** CAP <identifier> — the upstream primary id (shared across en/fr rows). */
  identifier: z.string(),
  /** CAP info <event> (free-text event name, e.g. "Tornado Warning"). */
  event: z.string().nullable(),
  severity: z.string().nullable(),
  urgency: z.string().nullable(),
  certainty: z.string().nullable(),
  /** Canonical IMS Response Level 1–4 (#376), DB-derived (migration 0098) from
   *  severity + the de-flood-safe life-safety event gate: Extreme→4; Severe→3 only
   *  for the life-safety event family (else 2); Moderate→2; Minor→1. The map, queue,
   *  and de-flood count all read THIS field — never re-derive it from severity. */
  response_level: z.number().int().nullable(),
  /** Human-readable region (concatenated <areaDesc>). */
  area_desc: z.string().nullable(),
  /** CAP <sent> envelope time (UTC ISO). */
  sent: z.string(),
  /** CAP info <effective>/<expires> (UTC ISO, nullable). */
  effective_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  instruction: z.string().nullable(),
  // Unified Anomaly Queue working-state (migration 0058, #140) — present on the
  // queue list endpoint; optional so detail-constructed features stay valid.
  work_acknowledged_at: z.string().nullable().optional(),
  micro_status: z.enum(['new', 'working', 'resolving']).optional(),
  owner_label: z.string().nullable().optional(),
  // R2/#478 incident-as-aggregator (migration 0119). The EFFECTIVE incident link:
  // the persisted MANUAL link (cap_alerts.incident_id) OR the read-time AUTO-link
  // projection (exactly one open incident within ~25 km). Non-null ⇒ this evac/
  // life-safety CAP has collapsed into that incident as a protective-action overlay
  // and is DROPPED from the standalone queue. Optional so detail-built features stay
  // valid; present on the list + detail endpoints.
  incident_id: z.string().nullable().optional(),
  // Provenance of the effective link (wire enum, distinct from the DB CHECK):
  // 'manual' = an operator persisted it via the dropdown; 'auto' = the read-time
  // projection; null = unlinked. (DB 'detached' never surfaces as a link.)
  incident_link_mode: z.enum(['manual', 'auto']).nullable().optional(),
});
export type CapAlertProperties = z.infer<typeof CapAlertPropertiesSchema>;

export const CapAlertFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: MultiPolygonSchema,
  properties: CapAlertPropertiesSchema,
});
export type CapAlertFeature = z.infer<typeof CapAlertFeatureSchema>;

export const CapAlertFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(CapAlertFeatureSchema),
});
export type CapAlertFeatureCollection = z.infer<typeof CapAlertFeatureCollectionSchema>;

/** Detail (`/:id`) — adds the full warning text + sender; geometry may be null
 *  (national / geocode-only alerts have no usable polygon). */
export const CapAlertDetailSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: MultiPolygonSchema.nullable(),
  properties: CapAlertPropertiesSchema.extend({
    sender: z.string().nullable(),
    sender_name: z.string().nullable(),
    status: z.string().nullable(),
    msg_type: z.string().nullable(),
    category: z.string().nullable(),
    headline: z.string().nullable(),
    description: z.string().nullable(),
  }),
});
export type CapAlertDetail = z.infer<typeof CapAlertDetailSchema>;
